import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import apiService from "../api/api.service";
import { useAuth } from "@/hooks/useAuth";

const WatchlistContext = createContext();

export const WatchlistProvider = ({ children }) => {
  const { isAuthenticated } = useAuth(); // Get authentication status

  const [state, setState] = useState({
    watchlists: [],
    loading: false,
    error: null,
  });

  const updateState = useCallback(
    (updates) => setState((prev) => ({ ...prev, ...updates })),
    []
  );

  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  const fetchWatchlists = useCallback(
    debounce(async () => {
      // Only make API call if user is authenticated
      if (!isAuthenticated) return;

      try {
        updateState({ loading: true, error: null });
        const response = await apiService.getUserWatchlists();
        updateState({
          watchlists: response.data || [],
          loading: false,
          error: null,
        });
      } catch (err) {
        updateState({
          error: err.message || "Failed to fetch watchlists",
          loading: false,
        });
      }
    }, 300),
    [isAuthenticated]
  ); // Add isAuthenticated as dependency

  useEffect(() => {
    // Only fetch data if user is authenticated
    if (isAuthenticated) {
      fetchWatchlists();
    } else {
      // Reset state when not authenticated
      setState({
        watchlists: [],
        loading: false,
        error: null,
      });
    }
  }, [fetchWatchlists, isAuthenticated]);

  // The rest of your functions with authentication checks
  const createWatchlist = async (watchlistData) => {
    if (!isAuthenticated) return false;

    try {
      updateState({ loading: true, error: null });
      const response = await apiService.createWatchlist({
        name: watchlistData.name,
        description: watchlistData.description,
        isPublic: watchlistData.isPublic || false,
      });
      await fetchWatchlists();
      updateState({ loading: false, error: null });
      return response;
    } catch (err) {
      updateState({
        error: err.message || "Failed to create watchlist",
        loading: false,
      });
      return false;
    }
  };

  const deleteWatchlist = async (watchlistId) => {
    if (!isAuthenticated) return false;

    try {
      updateState({ loading: true, error: null });
      const response = await apiService.deleteWatchlist(watchlistId);
      await fetchWatchlists();
      updateState({ loading: false, error: null });
      return response;
    } catch (err) {
      updateState({
        error: err.message || "Failed to delete watchlist",
        loading: false,
      });
      return false;
    }
  };

  const isProductInWatchlist = useCallback(
    (productId) => {
      if (!isAuthenticated || !productId) return false;

      return state.watchlists.some((watchlist) =>
        watchlist.products?.some(
          (product) =>
            (product.productId?._id || product.productId) === productId
        )
      );
    },
    [state.watchlists, isAuthenticated]
  );

  const addToWatchlist = async (
    watchlistId,
    productId,
    notes = "",
    alertSettings = null
  ) => {
    if (!isAuthenticated) return false;

    try {
      updateState({ loading: true, error: null });
      const response = await apiService.addProductToWatchlist({
        watchlistId,
        productId,
        notes,
        ...(alertSettings && { alertSettings }),
      });
      await fetchWatchlists();
      updateState({ loading: false, error: null });
      return response;
    } catch (err) {
      updateState({
        error: err.message || "Failed to add product to watchlist",
        loading: false,
      });
      return false;
    }
  };

  const removeFromWatchlist = async (watchlistId, productId) => {
    if (!isAuthenticated) return false;

    try {
      updateState({ loading: true, error: null });
      const response = await apiService.deleteProductFromWatchlist(
        watchlistId,
        productId
      );
      await fetchWatchlists();
      updateState({ loading: false, error: null });
      return response;
    } catch (err) {
      updateState({
        error: err.message || "Failed to remove product from watchlist",
        loading: false,
      });
      return false;
    }
  };

  const updateWatchlistAlerts = async (
    watchlistId,
    productId,
    alertSettings
  ) => {
    if (!isAuthenticated) return false;

    try {
      updateState({ loading: true, error: null });
      const response = await apiService.updateWatchlistAlerts({
        watchlistId,
        productId,
        alertSettings,
      });
      await fetchWatchlists();
      updateState({ loading: false, error: null });
      return response;
    } catch (err) {
      updateState({
        error: err.message || "Failed to update watchlist alerts",
        loading: false,
      });
      return false;
    }
  };

  const value = {
    watchlists: state.watchlists,
    loading: state.loading,
    error: state.error,
    isProductInWatchlist,
    addToWatchlist,
    removeFromWatchlist,
    createWatchlist,
    deleteWatchlist,
    fetchWatchlists,
    updateWatchlistAlerts,
  };

  return (
    <WatchlistContext.Provider value={value}>
      {children}
    </WatchlistContext.Provider>
  );
};

export const useWatchlist = () => {
  const context = useContext(WatchlistContext);
  if (!context)
    throw new Error("useWatchlist must be used within a WatchlistProvider");
  return context;
};
